<script setup>
import { useRoute } from 'vue-router'
import { onMounted, provide, ref, useTemplateRef } from 'vue'

import PollComponent from '@/components/CompiledPoll/PollComponent.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import ButtonSalva from '@/components/Generics/Buttons/ButtonSalva.vue'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import Loading from '@/components/Generics/Loadings/Loading.vue'

library.add(faCheck)

const route = useRoute()
const props = defineProps(['updatePageTitle'])
const compiledPollId = route['params']['id']
let compiledPoll = ref(null)
let isLoading = ref(true)
let form = ref(null)
let answers = ref([])

const modalConfirmChangeState = useTemplateRef('modal-confirm-change-state')
const currCompiledPoll = ref(null)
const currTransitionToApply = ref(null)

const modalSave = useTemplateRef('modal-save')
const loadingSave = ref(false)

const formErrors = ref([])
const questionsErrors = ref([])

const getCompiledPoll = async () => {
  try {
    let url = `/compiled-poll/api/compiled-poll/${compiledPollId}?poll=1`
    const response = await client.get(url)
    compiledPoll.value = response.data
    isLoading.value = false
  } catch (error) {
    toast.create('Errore nel caricamento dei dati, riprova più tardi', 'error')
    isLoading.value = false
  }
}

const handleAnswer = (questionReference, value, remove, note = null) => {
  if (!remove) {
    let exists = false
    answers.value.forEach(el => {
      if (el['questionReference'] === questionReference) {
        el.value = value
        el.note = note
        exists = true
      }
    })
    if (!exists) {
      answers.value.push({
        questionReference: questionReference,
        value: value,
        note: note
      })
    }
  } else {
    answers.value.forEach((el, index) => {
      if (el['questionReference'] === questionReference) {
        answers.value.splice(index, 1)
      }
    })
  }
}

const compilePoll = async (event, transition = null, confirm = false) => {
  if (!confirm) {
    currCompiledPoll.value = compiledPoll.value
    currTransitionToApply.value = transition
    modalConfirmChangeState.value.classList.add('d-block')
  } else {
    formErrors.value = []
    questionsErrors.value = []
    loadingSave.value = true
    let data = {
      userReference: currCompiledPoll.value['userReference'],
      // "state": state,
      campaign: currCompiledPoll.value['campaign'],
      answers: answers.value,
      pollTemplateRevisionNumber:
        currCompiledPoll.value['pollTemplateRevisionNumber'],
      attributes: currCompiledPoll.value['attributes']
    }
    try {
      let url = `/compiled-poll/api/compiled-poll/${currCompiledPoll.value['id']}?poll=1`

      await client.put(url, data)
      if (transition) {
        let url = `/compiled-poll/api/compiled-poll/${currCompiledPoll.value['id']}/workflow-transitions/${currTransitionToApply.value['key']}`
        await client.post(url)
        await getCompiledPoll()
      }

      currCompiledPoll.value = null
      currTransitionToApply.value = null
      modalConfirmChangeState.value.classList.remove('d-block')
      modalSave.value.classList.remove('d-block')

      toast.create(`Somministrazione <b>${compiledPoll.value.id}</b> aggiornata con successo`, 'success')
    } catch (error) {
      toast.create('Errore nel caricamento dei dati, riprova più tardi', 'error')

      if (error.data && error.data.errors) {
        for (let e of error.data.errors) {
          if (e.params['dpa.error.type'] === 'form') {
            formErrors.value.push(e)
          } else {
            questionsErrors.value.push(e)
          }
        }
      }

      modalConfirmChangeState.value.classList.remove('d-block')
      modalSave.value.classList.remove('d-block')

    }
    loadingSave.value = false
  }
}

const handleSaveModal = () => {
  modalSave.value.classList.add('d-block')
}

provide('questionsErrors', questionsErrors)

onMounted(async () => {
  await getCompiledPoll()
  let pageTitle = 'Compilazione - ' + compiledPoll.value['id']
  props.updatePageTitle(pageTitle)
})
</script>

<template>
  <div class="container-fluid" v-if="compiledPoll">
    <div class="row">
      <div class="col-12 px-0">
        <div class="card">

          <div
            v-if="formErrors.length"
            class="alert alert-danger mt-2 mx-2"
            role="alert"
          >
            <ul class="mb-0">
              <li v-for="e in formErrors" :key="e">
                {{ e.params['dpa.error.customMessage'] }}
              </li>
            </ul>
          </div>

          <loading v-if="isLoading"></loading>

          <form @submit.prevent="() => {}" ref="form" class="m-2">
            <poll-component
              v-if="compiledPoll"
              :compiled-poll="compiledPoll"
              :handle-answer="handleAnswer"
              :form="form"
            ></poll-component>
          </form>
        </div>

        <div class="d-flex mt-2 justify-content-between">

          <router-link :to="{ name: 'admin/CompiledPoll' }">
            <button-torna-alla-lista></button-torna-alla-lista>
          </router-link>

          <div class="ms-auto"></div>

          <button-salva
            v-if="compiledPoll['enabledActions'] && compiledPoll['enabledActions']['update']"
            @click="compilePoll">
          </button-salva>

          <button v-if="compiledPoll['enabledTransitions'].length"
                  class="btn btn-primary btn-sm ms-2" @click="handleSaveModal">
            Salva e cambia stato
          </button>

        </div>
      </div>
    </div>
  </div>
  <div class="modal" tabindex="-1" ref="modal-confirm-change-state">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" v-if="currTransitionToApply">Salvataggio compilazione e cambio stato</h5>
          <h5 class="modal-title" v-else>Salvataggio compilazione</h5>
          <button
            v-if="!loadingSave"
            type="button"
            class="btn-close border-0 bg-white"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="()=>{modalConfirmChangeState.classList.remove('d-block')}"
          ></button>
        </div>
        <div class="modal-body" v-if="currCompiledPoll">
          <p v-if="currTransitionToApply">La compilazione verrà salvata e le verrà applicato il cambio di stato
            "<b>{{ currTransitionToApply['labels']['it'] }}</b>", procedere?</p>
          <p v-else>La compilazione verrà salvata, procedere?</p>
        </div>
        <div class="modal-footer">
          <button
            v-if="!loadingSave"
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="()=>{modalConfirmChangeState.classList.remove('d-block')}"
          >
            Annulla
          </button>
          <button type="button" :disabled="loadingSave" class="btn btn-primary"
                  @click="compilePoll($event, currTransitionToApply, true)">Procedi
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal" tabindex="-1" ref="modal-save">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Quale transizione applicare?</h5>
          <button
            type="button"
            class="btn-close border-0 bg-white"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="()=>{modalSave.classList.remove('d-block')}"
          ></button>
        </div>
        <div class="modal-body" v-if="compiledPoll">
          <ul class="list-group list-group-flush">
            <li class="list-group-item px-0" v-for="et in compiledPoll['enabledTransitions']" :key="et">
              <div class="d-flex justify-content-between">
                Salva e applica "{{ et['labels']['it'] }}"
                <button class="btn btn-primary btn-sm" @click="compilePoll($event, et)">
                  <font-awesome-icon icon="fa-check" />
                </button>
              </div>
            </li>
          </ul>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="()=>{modalSave.classList.remove('d-block')}"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

:root,
:host {
  @extend :root;
}

[class*='fa-'] {
  width: 0.9rem;
}
</style>
